/**
 * Dispatch Board API (mock-store implementation for S1).
 *
 * Activity-centric model — see types.ts and mock-store.ts. Every function
 * here mirrors a planned REST endpoint; the S2 swap replaces each body
 * with an axios call without touching the hooks layer above.
 */

import type {
  ActivityAttachment,
  BacklogTaskCreatePayload,
  ComplianceActivityMeta,
  CaptureActivityPayload,
  CaptureDayCartageRequest,
  CaptureSummary,
  CrewColorToken,
  DispatchCrew,
  DispatchCrewJobActivity,
  DispatchJobType,
  DispatchVehicle,
  RollOverPayload,
  UnassignedJob,
} from '../types';
import { getLoggedinUser } from '../../../helpers/api_helper';
import { mockStore } from './mock-store';
import { useRealApi } from './backend/feature-flag';
import { backendApi } from './backend/services';
import { DispatchTaskStatus } from './backend/enums';
import { mapBackendDayToFrontend } from './backend/mappers';

/**
 * Pull tenantId from the persisted login. Throws when absent — every
 * real-API write requires a tenant, and silently dropping the field
 * would surface as an opaque 400 from the server. Fail loud here.
 */
function requireTenantId(): string {
  const t = getLoggedinUser()?.tenantId;
  if (!t) {
    throw new Error(
      '[dispatch-board] Missing tenantId on logged-in user — please sign in again.',
    );
  }
  return t;
}

/**
 * Promote a CreateUpdateResponse's `isSuccess === false` into a thrown
 * Error so react-query's mutation `onError` fires. The default axios
 * promise resolves on HTTP 200 even when the body says the write failed
 * (the backend prefers explicit error shapes over status codes).
 */
function ensureOk(res: { isSuccess: boolean; message?: string; modelErrors?: unknown }): void {
  if (res.isSuccess) return;
  const detail =
    res.message ||
    (res.modelErrors ? JSON.stringify(res.modelErrors) : 'Unknown server error');
  throw new Error(`[dispatch-board] Backend rejected request: ${detail}`);
}

/**
 * Reconstruct a File from a base64 dataUrl. The frontend stores
 * attachments as `{name, type, dataUrl}` (memory-only, client-side
 * preview); the backend's multipart upload needs an actual File. This
 * round-trips via fetch+blob — works in all modern browsers, no extra
 * polyfill needed.
 */
async function dataUrlToFile(att: ActivityAttachment): Promise<File> {
  const response = await fetch(att.dataUrl);
  const blob = await response.blob();
  return new File([blob], att.name, { type: att.type || blob.type });
}

/**
 * Map the frontend's DispatchJobType string to the backend's
 * DispatchActionIntent enum value. Mirrors the inverse of
 * `mappers.ts:mapTaskType` so a round-trip stays stable.
 */
function jobTypeToActionIntent(type: DispatchJobType): number {
  switch (type) {
    case 'ERECT': return 1;     // Erect
    case 'MODIFY': return 2;    // Modify
    case 'DISMANTLE': return 3; // Dismantle
    case 'INSPECT': return 5;   // Inspection
    case 'HANDOVER': return 6;  // Maintenance (UI's "HANDOVER" maps to backend Maintenance — pre-existing quirk, see mapper)
    default: return 0;           // None
  }
}

/**
 * Map the frontend's DispatchJobType string to the backend's
 * ScaffoldTaskType enum. For an UnassignedJob without an explicit
 * `taskType` enum number, derive from the `type` string.
 */
function jobTypeToScaffoldTaskType(t: UnassignedJob): number {
  if (typeof t.taskType === 'number') return t.taskType;
  switch (t.type) {
    case 'ERECT': return 1;
    case 'MODIFY': return 2;
    case 'DISMANTLE': return 3;
    case 'INSPECT': return 4;
    case 'HANDOVER': return 5; // Maintenance (UI quirk)
    default: return 6;          // General
  }
}

const LATENCY_MS = 60;
const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export const dispatchApi = {
  /**
   * Day view composite read.
   *
   * Phase 7b: dispatches between mock store and real API per the
   * `VITE_USE_MOCK_STORE` flag (or `localStorage.useMockStore`). Real-API
   * mode adds the optional `unplannedTasksFromBackend` field; the hook
   * layer prefers it when present so the rail inherits the backend's
   * "never Completed" visibility rule. Mock mode omits that field and the
   * hook falls back to filtering `useUnplannedWork` by consumed ids.
   */
  async getDay(date: string): Promise<{
    crews: DispatchCrew[];
    standaloneActivities: DispatchCrewJobActivity[];
    consumedUnassignedIds: string[];
    /** Present only in real-API mode. Hook prefers this over filtering useUnplannedWork. */
    unplannedTasksFromBackend?: UnassignedJob[];
    /** Present only in mock mode (Sprint A) — manually-created backlog tasks
     *  the hook merges into the Unassigned rail. Real mode returns these via
     *  the backend's own unplanned list, so this stays undefined there. */
    backlogTasks?: UnassignedJob[];
  }> {
    if (useRealApi()) {
      // No artificial latency on the real path — the network IS the latency.
      const raw = await backendApi.board.getDay(date);
      return mapBackendDayToFrontend(raw);
    }
    await wait(LATENCY_MS);
    return mockStore.getDay(date);
  },

  /* ---------- Crew CRUD ---------- */
  /**
   * Create a new crew on the given date. Mock-store generates a default
   * "Crew N" name when blank; in real-API mode the caller (hook) is
   * expected to pre-compute the name from cache so the request always
   * carries an explicit value (the backend has no auto-naming).
   */
  async createCrew(
    date: string,
    payload: { name?: string; colorToken?: CrewColorToken },
  ): Promise<void> {
    if (useRealApi()) {
      const tenantId = requireTenantId();
      const res = await backendApi.crew.create({
        tenantId,
        date,
        name: payload.name?.trim() || 'New Crew',
        colorToken: payload.colorToken,
        position: null,
      });
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.createCrew(date, payload);
  },
  async deleteCrew(date: string, crewId: string): Promise<void> {
    if (useRealApi()) {
      const res = await backendApi.crew.delete(crewId);
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.deleteCrew(date, crewId);
  },
  /**
   * Rename a crew. `opts.rowVersion` is the optimistic-concurrency token
   * the hook reads from the cached DispatchCrew. Backend skips the check
   * when undefined (see CrewRepository.Update line 169).
   */
  async renameCrew(
    date: string,
    crewId: string,
    name: string,
    opts: { rowVersion?: string } = {},
  ): Promise<void> {
    if (useRealApi()) {
      const res = await backendApi.crew.update(crewId, {
        name,
        rowVersion: opts.rowVersion ?? null,
      });
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.renameCrew(date, crewId, name);
  },
  async setCrewColor(
    date: string,
    crewId: string,
    colorToken: CrewColorToken,
    opts: { rowVersion?: string } = {},
  ): Promise<void> {
    if (useRealApi()) {
      const res = await backendApi.crew.update(crewId, {
        colorToken,
        rowVersion: opts.rowVersion ?? null,
      });
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.setCrewColor(date, crewId, colorToken);
  },

  /* ---------- Members ---------- */
  /**
   * Add a user to a crew. `opts.isLead = true` makes them the lead in the
   * same request (backend's AddMember handles the transactional lead
   * swap when needed — CrewRepository.AddMember line 349). Hook layer
   * passes `isLead = true` when the destination crew currently has zero
   * members so the first-member-becomes-lead behaviour matches mock.
   * `member.name` + `member.role` are UI-side enrichment; the backend
   * derives userName from DbUser on the next read.
   */
  async addMember(
    date: string,
    crewId: string,
    member: { userId: string; name: string; role?: string },
    opts: { isLead?: boolean } = {},
  ): Promise<void> {
    if (useRealApi()) {
      const tenantId = requireTenantId();
      const res = await backendApi.crew.addMember(crewId, {
        tenantId,
        crewId,
        userId: member.userId,
        isLead: opts.isLead ?? false,
      });
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.addMember(date, crewId, member);
  },
  /**
   * Remove a user from a crew. Real-API needs the DbCrewMember row id
   * (`memberRowId`), NOT userId — the hook layer looks it up from the
   * cached DispatchCrewMember and passes it through. Falls back to a
   * GetMembers round-trip if the cache doesn't carry it (defensive —
   * shouldn't fire in practice).
   */
  async removeMember(
    date: string,
    crewId: string,
    userId: string,
    opts: { memberRowId?: string } = {},
  ): Promise<void> {
    if (useRealApi()) {
      let memberRowId = opts.memberRowId;
      if (!memberRowId) {
        // Defensive fallback: the hook should always provide memberRowId
        // from cache. If it doesn't, fetch the members list to find it.
        const members = await backendApi.crew.getMembers(crewId);
        memberRowId = members.find((m) => m.userId === userId)?.id;
        if (!memberRowId) {
          throw new Error(
            `[dispatch-board] No member row found for user ${userId} on crew ${crewId}`,
          );
        }
      }
      const res = await backendApi.crew.removeMember(memberRowId);
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.removeMember(date, crewId, userId);
  },
  /**
   * Atomic lead change. Backend transactionally demotes the current lead
   * (if any) and promotes the new one — see CrewRepository.SetLead
   * (line 588 in the controller's referenced repo). Pass userId = '' to
   * clear the lead without promoting anyone (mock semantics use the
   * same null/'' convention).
   */
  async setLead(date: string, crewId: string, userId: string): Promise<void> {
    if (useRealApi()) {
      const res = await backendApi.crew.setLead({
        crewId,
        userId: userId || null,
      });
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.setLead(date, crewId, userId);
  },

  /* ---------- Vehicles / assets ---------- */
  /**
   * Attach a tenant asset to a crew. Only `vehicle.assetId` is sent to
   * the backend — the rest (code, label, assetClass, imageUrl) are
   * UI-side enrichment from the tenant asset lookup and the backend
   * re-derives them on the next read.
   */
  async addVehicle(
    date: string,
    crewId: string,
    vehicle: DispatchVehicle,
  ): Promise<void> {
    if (useRealApi()) {
      const tenantId = requireTenantId();
      const res = await backendApi.crew.addAsset(crewId, {
        tenantId,
        crewId,
        assetId: vehicle.assetId,
      });
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.addVehicle(date, crewId, vehicle);
  },
  /**
   * Detach an asset from a crew. Real-API needs the DbCrewAsset row id
   * (`rowId`), NOT the tenant assetId — hook layer looks it up from the
   * cached DispatchVehicle (populated by the Phase 7c mapper change).
   * Falls back to a GetAssets round-trip if cache doesn't carry it.
   */
  async removeVehicle(
    date: string,
    crewId: string,
    assetId: string,
    opts: { rowId?: string } = {},
  ): Promise<void> {
    if (useRealApi()) {
      let rowId = opts.rowId;
      if (!rowId) {
        const assets = await backendApi.crew.getAssets(crewId);
        rowId = assets.find((a) => a.assetId === assetId)?.id;
        if (!rowId) {
          throw new Error(
            `[dispatch-board] No crew-asset row found for asset ${assetId} on crew ${crewId}`,
          );
        }
      }
      const res = await backendApi.crew.removeAsset(rowId);
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.removeVehicle(date, crewId, assetId);
  },

  /* ---------- Activity lifecycle (the new core) ---------- */
  async createActivity(
    date: string,
    payload: {
      clientId: string;
      clientName: string;
      siteId: string;
      siteName: string;
      /** Parent Job — required at creation. */
      jobId?: string;
      jobNumber?: string;
      /** Parent Job's chargeUpType enum (1-4). */
      jobChargeUpType?: number;
      /** Hex colour of the picked site's SiteType. */
      siteTypeColorHex?: string;
      title: string;
      type: DispatchJobType;
      crewId?: string;
      /** Free-text dispatcher notes (optional). */
      notes?: string;
      /** N tasks to link into the new activity at creation time. */
      initialTasks?: UnassignedJob[];
      /** Files + photos attached at creation. */
      attachments?: ActivityAttachment[];
      /** Unplanned-work flag (phone-call style — no Quote, no Job picker). */
      isUnplanned?: boolean;
      /** Description of the unplanned work. Required when isUnplanned. */
      unplannedDescription?: string;
      /** Compliance-check flag + payload (mock prototype — passed through to the store). */
      isCompliance?: boolean;
      compliance?: ComplianceActivityMeta;
    },
  ): Promise<void> {
    if (useRealApi()) {
      const tenantId = requireTenantId();

      // Backend has no separate `unplannedDescription` column — surface
      // it through Notes when the activity has no parent Job (matches
      // the mapper's read-side derivation in Phase 7b).
      const effectiveNotes = payload.isUnplanned
        ? (payload.unplannedDescription?.trim() || payload.notes?.trim() || null)
        : (payload.notes?.trim() || null);

      // Backend requires explicit ISO ScheduledStart. Mock-store defaults
      // to 08:00-17:00 on the picked date; mirror that here so demos look
      // identical until time-of-day pickers ship.
      const scheduledStart = `${date}T08:00:00`;
      const scheduledEnd = `${date}T17:00:00`;

      // Derive ActionIntent from the picked type (popup's "Erect/Modify/…"
      // chip). Backend stores this so the audit shows what the dispatcher
      // INTENDED — useful when a task changes type mid-day.
      const actionIntent = jobTypeToActionIntent(payload.type);

      // initialTasks → backend's TaskCreateRequest shape. The repo wires
      // up dispatchActivityId from the new row on the server (the value
      // we send is overwritten — pass the empty guid as a placeholder).
      const initialTasks = (payload.initialTasks ?? []).map((t) => ({
        tenantId,
        dispatchActivityId: '00000000-0000-0000-0000-000000000000',
        workOrderTaskId: t.id,
        // Forward the task's scaffold link so the persisted
        // DbDispatchActivityTask.ScaffoldId is set. Without it the day-read's
        // `t.Scaffold?.ScaffoldNumber` / `…WorkOrder?.WorkOrderNumber` resolve
        // to null and the capture form falls back to the bare Job number.
        scaffoldId: t.scaffoldId ?? null,
        scaffoldTaskType: jobTypeToScaffoldTaskType(t),
        notes: null,
      }));

      const res = await backendApi.activity.create({
        tenantId,
        crewId: payload.crewId ?? null,
        clientId: payload.clientId || null,
        siteId: payload.siteId || null,
        jobId: payload.jobId ?? null,
        sourceWorkOrderTaskId: null,
        title: payload.title,
        activityType: 1, // Job (the only type the dispatch popup creates today)
        actionIntent,
        scheduledStart,
        scheduledEnd,
        planningOrder: null,
        notes: effectiveNotes,
        initialTasks,
      });
      ensureOk(res);

      // Two-phase attachment upload — activity row first (above), then
      // batch-upload files in a second request. Backend has no atomic
      // create-with-attachments path (different storage subsystem). If
      // attachment upload fails, the activity still exists; toast surfaces
      // the partial error and dispatcher can retry via Edit.
      const newActivityId = res.id ?? res.result?.id;
      if (newActivityId && payload.attachments && payload.attachments.length > 0) {
        const files = await Promise.all(
          payload.attachments.map((a) => dataUrlToFile(a)),
        );
        const batchRes = await backendApi.attachment.createBatch(
          tenantId,
          newActivityId,
          files,
        );
        if (!batchRes.overallSuccess) {
          throw new Error(
            `[dispatch-board] Activity created but ${payload.attachments.length} attachment(s) failed to upload. Edit the activity to retry.`,
          );
        }
      }
      return;
    }
    await wait(LATENCY_MS);
    mockStore.createActivity(date, payload);
  },
  /**
   * Pin a standalone activity to a crew. Backend's PUT with
   * `reassignCrew: true` triggers Rule 5 server-side: it auto-replaces
   * the activity's worker + asset roster with the destination crew's
   * defaults (matches mock-store's `moveActivityBetweenCrews` semantic).
   */
  async assignActivityToCrew(
    date: string,
    activityId: string,
    crewId: string,
  ): Promise<void> {
    if (useRealApi()) {
      const res = await backendApi.activity.update(activityId, {
        crewId,
        reassignCrew: true,
      });
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.assignActivityToCrew(date, activityId, crewId);
  },
  async deleteActivity(date: string, activityId: string): Promise<void> {
    if (useRealApi()) {
      const res = await backendApi.activity.delete(activityId);
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.deleteActivity(date, activityId);
  },
  /**
   * Patch an activity. Real-API mode handles three concerns in one call:
   *   1. Core fields → single PUT (title, notes, jobId, newDate)
   *   2. Tasks diff → POST new + DELETE removed via task subresource
   *   3. Attachments diff → POST new + DELETE removed via attachment endpoint
   *
   * The hook layer is expected to pass `currentTasks` and `currentAttachments`
   * from the cached activity so diffing doesn't require an extra GET. Each
   * diff fires only when its corresponding `patch.initialTasks` /
   * `patch.attachments` is defined (undefined = leave alone).
   */
  async updateActivity(
    date: string,
    activityId: string,
    patch: {
      title?: string;
      notes?: string;
      jobId?: string | null;
      jobNumber?: string | null;
      jobChargeUpType?: number | null;
      /** New date YYYY-MM-DD — only honoured for standalone activities. */
      newDate?: string;
      initialTasks?: UnassignedJob[];
      /** Full desired attachment list — REPLACES the existing one. */
      attachments?: ActivityAttachment[];
      /** Refine the unplanned description (only meaningful for unplanned activities). */
      unplannedDescription?: string;
    },
    opts: {
      /** Optimistic-concurrency token from the cached activity. */
      rowVersion?: string;
      /** Current attachment-row ids from cache, for delete-diffing. */
      currentAttachments?: Array<{ id: string; name: string }>;
      /** Current task workOrderTaskIds from cache, for delete-diffing.
       *  Map of workOrderTaskId → taskRowId (the DbDispatchActivityTask.Id). */
      currentTaskRowIds?: Record<string, string>;
      /**
       * True if `currentAttachments` / `currentTaskRowIds` were populated
       * from a live cache hit; false if cache was empty/stale. When false
       * AND a task or attachment diff is requested, client.ts does a fresh
       * GET on the activity to avoid duplicating rows (empty maps in cache
       * miss are indistinguishable from "legitimately no tasks", so we
       * MUST refetch when in doubt).
       */
      cacheHit?: boolean;
    } = {},
  ): Promise<void> {
    if (useRealApi()) {
      const tenantId = requireTenantId();

      // ── 1. Core PUT (only if any core field actually changed)
      const hasCoreChange =
        patch.title !== undefined ||
        patch.notes !== undefined ||
        patch.unplannedDescription !== undefined ||
        patch.jobId !== undefined ||
        patch.newDate !== undefined;

      if (hasCoreChange) {
        // Backend's Notes-update rule (DispatchActivityRepository.cs:498):
        //   `if (updateRequest.Notes != null) toUpdate.Notes = updateRequest.Notes;`
        // So `null` means "leave alone" and `""` means "clear". Crucial:
        // don't collapse trimmed-empty to null — that would silently
        // ignore the user's clear-the-notes action. (unplannedDescription
        // rides on Notes when the activity has no Job, mirroring the
        // read-side derivation in Phase 7b's mapper.)
        let effectiveNotes: string | undefined;
        if (patch.unplannedDescription !== undefined) {
          effectiveNotes = patch.unplannedDescription.trim();
        } else if (patch.notes !== undefined) {
          effectiveNotes = patch.notes.trim();
        }

        const scheduledStart = patch.newDate
          ? `${patch.newDate}T08:00:00`
          : undefined;
        const scheduledEnd = patch.newDate
          ? `${patch.newDate}T17:00:00`
          : undefined;

        // jobId: explicit null means "clear it" (clearJobId flag);
        // undefined means "leave alone"; a string means "set to this".
        const clearJobId = patch.jobId === null;
        const jobId =
          patch.jobId === null ? null : patch.jobId ?? null;

        const res = await backendApi.activity.update(activityId, {
          title: patch.title ?? null,
          // undefined → null (backend skips); "" stays "" (backend clears);
          // "text" stays "text" (backend sets).
          notes: effectiveNotes !== undefined ? effectiveNotes : null,
          jobId,
          clearJobId,
          scheduledStart: scheduledStart ?? null,
          scheduledEnd: scheduledEnd ?? null,
          rowVersion: opts.rowVersion ?? null,
        });
        ensureOk(res);
      }

      // Cache-miss safety: if the hook couldn't read cached state but a
      // task or attachment diff is requested, fetch the activity fresh.
      // Without this, empty `currentTaskRowIds` / `currentAttachments`
      // would be misread as "activity has nothing" and the diff would
      // re-POST every desired task and attachment — duplicating rows on
      // the server.
      let effectiveTaskRowIds = opts.currentTaskRowIds;
      let effectiveAttachments = opts.currentAttachments;
      const needsDiff =
        patch.initialTasks !== undefined || patch.attachments !== undefined;
      if (needsDiff && opts.cacheHit === false) {
        const fresh = await backendApi.activity.getSingle(activityId);
        effectiveTaskRowIds = {};
        for (const t of fresh.tasks ?? []) {
          effectiveTaskRowIds[t.workOrderTaskId] = t.id;
        }
        effectiveAttachments = (fresh.attachments ?? []).map((a) => ({
          id: a.id,
          name: a.fileName,
        }));
      }

      // ── 2. Task diff (only if patch.initialTasks is defined)
      if (patch.initialTasks !== undefined) {
        const desiredIds = new Set(patch.initialTasks.map((t) => t.id));
        const currentMap = effectiveTaskRowIds ?? {};
        const currentIds = new Set(Object.keys(currentMap));

        // Remove tasks present in current but not in desired
        for (const woTaskId of currentIds) {
          if (!desiredIds.has(woTaskId)) {
            const taskRowId = currentMap[woTaskId];
            const res = await backendApi.activity.removeTask(taskRowId);
            ensureOk(res);
          }
        }
        // Add tasks present in desired but not in current
        for (const t of patch.initialTasks) {
          if (!currentIds.has(t.id)) {
            const res = await backendApi.activity.addTask(activityId, {
              tenantId,
              dispatchActivityId: activityId,
              workOrderTaskId: t.id,
              // Carry the scaffold link (see create path) so WO/Scaffold
              // resolve on the day-read instead of falling back to the Job no.
              scaffoldId: t.scaffoldId ?? null,
              scaffoldTaskType: jobTypeToScaffoldTaskType(t),
              notes: null,
            });
            ensureOk(res);
          }
        }
      }

      // ── 3. Attachment diff (only if patch.attachments is defined)
      if (patch.attachments !== undefined) {
        // The cache carries backend ids on attachments that came from a
        // read (mapper populates them). Newly-added attachments in the
        // popup have client-generated ids that aren't present in the
        // cached list. We diff by id.
        const desiredById = new Map(patch.attachments.map((a) => [a.id, a]));
        const currentList = effectiveAttachments ?? [];

        // Delete: in current but NOT in desired
        for (const cur of currentList) {
          if (!desiredById.has(cur.id)) {
            const res = await backendApi.attachment.delete(cur.id);
            ensureOk(res);
          }
        }
        // Upload: in desired but NOT in current (i.e., new uploads)
        const currentIds = new Set(currentList.map((c) => c.id));
        const newOnes = patch.attachments.filter((a) => !currentIds.has(a.id));
        if (newOnes.length > 0) {
          const files = await Promise.all(newOnes.map(dataUrlToFile));
          const batchRes = await backendApi.attachment.createBatch(
            tenantId,
            activityId,
            files,
          );
          if (!batchRes.overallSuccess) {
            throw new Error(
              `[dispatch-board] Update succeeded but ${newOnes.length} attachment(s) failed to upload. Try uploading again.`,
            );
          }
        }
      }
      return;
    }
    await wait(LATENCY_MS);
    mockStore.updateActivity(date, activityId, patch);
  },
  /**
   * Detach an activity from its crew (back to the standalone rail).
   * `reassignCrew: true` + `crewId: null` clears the link; Rule 5 also
   * fires server-side and soft-deletes the old crew's roster from the
   * activity (matches mock's "drop workers/assets when un-pinned").
   */
  async unassignActivityFromCrew(
    date: string,
    activityId: string,
  ): Promise<void> {
    if (useRealApi()) {
      const res = await backendApi.activity.update(activityId, {
        crewId: null,
        reassignCrew: true,
      });
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.unassignActivityFromCrew(date, activityId);
  },
  /**
   * Move an activity from crew A to crew B. Same backend call as
   * assignActivityToCrew — the repo doesn't distinguish "first assign"
   * from "reassign", it just sets `CrewId = newValue` under the
   * ReassignCrew flag and re-syncs the roster.
   */
  async moveActivityBetweenCrews(
    date: string,
    activityId: string,
    toCrewId: string,
  ): Promise<void> {
    if (useRealApi()) {
      const res = await backendApi.activity.update(activityId, {
        crewId: toCrewId,
        reassignCrew: true,
      });
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.moveActivityBetweenCrews(date, activityId, toCrewId);
  },

  /* ---------- Task ↔ Activity linkage ---------- */
  /**
   * Link an unplanned WorkOrderTask onto an activity. Backend's AddTask
   * enforces JOB-MATCH (task's parent Job must match activity's Job) +
   * JOB-INHERIT (if activity has no Job, adopts the task's) server-side
   * — matches mock's client-side rules. Any violation surfaces as
   * `isSuccess: false` and `ensureOk` throws.
   */
  async linkTaskToActivity(
    date: string,
    activityId: string,
    task: UnassignedJob,
  ): Promise<void> {
    if (useRealApi()) {
      const tenantId = requireTenantId();
      const res = await backendApi.activity.addTask(activityId, {
        tenantId,
        dispatchActivityId: activityId,
        workOrderTaskId: task.id,
        // Carry the scaffold link (see create path) so WO/Scaffold resolve on
        // the day-read instead of falling back to the bare Job number.
        scaffoldId: task.scaffoldId ?? null,
        scaffoldTaskType: jobTypeToScaffoldTaskType(task),
        notes: null,
      });
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.linkTaskToActivity(date, activityId, task);
  },
  /**
   * Unlink a task from an activity. `taskId` here IS the row id — the
   * mapper populates `DispatchActivityTask.taskId` from backend's
   * `DbDispatchActivityTask.Id`, so no cache lookup is required.
   */
  async unlinkTaskFromActivity(
    date: string,
    activityId: string,
    taskId: string,
  ): Promise<void> {
    if (useRealApi()) {
      const res = await backendApi.activity.removeTask(taskId);
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.unlinkTaskFromActivity(date, activityId, taskId);
  },

  /* ---------- Job workers ---------- */
  /**
   * Add a worker (staff drag onto activity card). `worker.name` is FE
   * enrichment; backend derives userName from `DbUser` on next read.
   */
  async addJobWorker(
    date: string,
    activityId: string,
    worker: { userId: string; name: string },
  ): Promise<void> {
    if (useRealApi()) {
      const tenantId = requireTenantId();
      const res = await backendApi.activity.addWorker(activityId, {
        tenantId,
        dispatchActivityId: activityId,
        userId: worker.userId,
      });
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.addJobWorker(date, activityId, worker);
  },
  /**
   * Remove a worker. Backend keys off DbDispatchActivityWorker.Id (the
   * row id), NOT userId. Hook layer pulls `workerRowId` from the cached
   * worker (populated by Phase 7e mapper). Defensive `getWorkers`
   * fallback if the cache doesn't carry it.
   */
  async removeJobWorker(
    date: string,
    activityId: string,
    userId: string,
    opts: { workerRowId?: string } = {},
  ): Promise<void> {
    if (useRealApi()) {
      let workerRowId = opts.workerRowId;
      if (!workerRowId) {
        const workers = await backendApi.activity.getWorkers(activityId);
        workerRowId = workers.find((w) => w.userId === userId)?.id;
        if (!workerRowId) {
          throw new Error(
            `[dispatch-board] No worker row found for user ${userId} on activity ${activityId}`,
          );
        }
      }
      const res = await backendApi.activity.removeWorker(workerRowId);
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.removeJobWorker(date, activityId, userId);
  },

  /**
   * Add an asset directly to an activity (the "extra vehicle for this
   * specific activity" use case, separate from crew-level assets). Only
   * `vehicle.assetId` is sent; code/label/assetClass/imageUrl are FE
   * enrichment from the tenant asset lookup.
   */
  async addJobVehicle(
    date: string,
    activityId: string,
    vehicle: {
      assetId: string;
      code: string;
      label: string;
      assetClass?: string;
      imageUrl?: string;
    },
  ): Promise<void> {
    if (useRealApi()) {
      const tenantId = requireTenantId();
      const res = await backendApi.activity.addAsset(activityId, {
        tenantId,
        dispatchActivityId: activityId,
        assetId: vehicle.assetId,
      });
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.addJobVehicle(date, activityId, vehicle);
  },
  /**
   * Remove an asset from an activity. Backend keys off
   * DbDispatchActivityAsset.Id (the row id, NOT the tenant assetId).
   * Hook layer pulls `rowId` from the cached vehicle entry (mapper
   * populated by Phase 7c). Defensive `getAssets` fallback if missing.
   */
  async removeJobVehicle(
    date: string,
    activityId: string,
    assetId: string,
    opts: { rowId?: string } = {},
  ): Promise<void> {
    if (useRealApi()) {
      let rowId = opts.rowId;
      if (!rowId) {
        const assets = await backendApi.activity.getAssets(activityId);
        rowId = assets.find((a) => a.assetId === assetId)?.id;
        if (!rowId) {
          throw new Error(
            `[dispatch-board] No activity-asset row found for asset ${assetId} on activity ${activityId}`,
          );
        }
      }
      const res = await backendApi.activity.removeAsset(rowId);
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.removeJobVehicle(date, activityId, assetId);
  },

  /* ---------- Copy Day — clone crews from another date ---------- */

  /**
   * Clone crews from one date to another. Append-only on the target date.
   *
   * SEMANTIC MISMATCH (mock vs real-API): the backend's CopyDay clones
   * ALL crews from the source date — there is no `crewIds` selector
   * (see `DispatchBoardService.CopyDay`). In real-API mode we therefore
   * IGNORE `options.crewIds` and the user gets every source crew. The
   * dispatcher's modal selection is effectively a "preview" of what
   * they'll get when they hit Copy. We `console.warn` when the picked
   * subset is smaller than the actual cloned count so this divergence
   * is visible in dev tools. Backend also refuses to copy onto a target
   * date that already has crews (server-side guard).
   */
  async cloneCrews(
    fromDate: string,
    toDate: string,
    options: { crewIds: string[] },
  ): Promise<{ clonedCount: number }> {
    if (useRealApi()) {
      const res = await backendApi.board.copyDay(fromDate, toDate);
      ensureOk(res);
      const clonedCount = res.result ?? 0;
      if (clonedCount !== options.crewIds.length) {
        // Either > (backend ignored our selector + cloned extras) OR <
        // (source crews disappeared between picker render and submit).
        // Both are worth flagging in dev tools.
        // eslint-disable-next-line no-console
        console.warn(
          `[dispatch-board] Copy Day count mismatch — ` +
            `requested ${options.crewIds.length}, backend cloned ${clonedCount}. ` +
            `Backend clones ALL source crews (no selector); delete unwanted ` +
            `crews manually if the picker showed a subset.`,
        );
      }
      return { clonedCount };
    }
    await wait(LATENCY_MS);
    return mockStore.cloneCrews(fromDate, toDate, options);
  },

  /* ---------- Bulk staff removal ---------- */

  /**
   * Mark a user away for the day.
   *
   * SEMANTIC MISMATCH (mock vs real-API): mock strips the user from
   * BOTH crew member rosters AND every activity worker row. Backend's
   * MarkStaffAway only deactivates today's worker rows on activities —
   * the deliberate design choice is "crews are per-day templates;
   * tomorrow's crew is unaffected, so removing today's activity
   * assignments is enough" (see DispatchBoardService.MarkStaffAway
   * comment). So in real-API mode:
   *   - Activities: user removed from today's worker rosters ✓
   *   - Crews: user REMAINS on the crew member list
   * The optimistic patch in `useMarkStaffAway` is mode-aware so the
   * displayed state matches the post-refetch reality (no flicker).
   * Toast shows `crewsRemovedFrom: 0` truthfully in real-API mode.
   */
  async markStaffAway(
    date: string,
    userId: string,
  ): Promise<{ crewsRemovedFrom: number; activitiesRemovedFrom: number }> {
    if (useRealApi()) {
      const res = await backendApi.board.markStaffAway(userId, date);
      ensureOk(res);
      return {
        crewsRemovedFrom: 0, // backend doesn't touch crew memberships
        activitiesRemovedFrom: res.result ?? 0,
      };
    }
    await wait(LATENCY_MS);
    return mockStore.markStaffAway(date, userId);
  },

  /* ---------- End-of-day Capture + Roll-over (Phase 7g — mock-first) ----------
   *
   * MOCK-ONLY for now. The real backend endpoints exist (Phase 5c) but we
   * deliberately keep the FE on the mock so the client can validate the UX
   * flow first. Real-API wiring lands in Phase 7g-REAL after sign-off,
   * following the same `useRealApi()` swap pattern as 7c-7f.
   */

  /**
   * Apply hour entries + per-task status + per-asset km to an activity.
   *
   * Real path (Phase 7g-REAL): POST /DispatchActivity/capture. The backend
   * persists the captured ScaffoldTask statuses and then cascades
   * ScaffoldTask → Scaffold → WorkOrder → Job (G1), so capturing work
   * advances the whole lifecycle.
   *
   * Attach-at-capture / ad-hoc-at-capture are mock-only conveniences — the
   * backend capture request carries no such fields (those go through the
   * addTask + ad-hoc ScaffoldTask paths). On the real path we fail loud
   * rather than silently drop billable work.
   */
  async captureActivity(date: string, payload: CaptureActivityPayload): Promise<void> {
    if (useRealApi()) {
      // Phase 7g-REAL: Job-mapping + linked + day-works tasks are now wired.
      // The backend materialises attach/adhoc into real DispatchActivityTask
      // rows (day-works gets a manual WorkOrderTask under the scaffold's WO)
      // and bills them through the normal labour pipeline.
      const res = await backendApi.activity.captureActivity({
        activityId: payload.activityId,
        jobId: payload.jobId ?? null,
        travelHours: payload.travelHours ?? null,
        loadingHours: payload.loadingHours ?? null,
        standbyHours: payload.standbyHours ?? null,
        taskCaptures: (payload.taskCaptures ?? []).map((t) => ({
          taskId: t.taskId,
          status: DispatchTaskStatus[t.status],
          perWorkerHours: t.perWorkerHours ?? null,
          // Per-rate-band hours override ("split by role"); null = uniform.
          roleHours: t.roleHours ?? null,
          notes: t.notes ?? null,
        })),
        assetCaptures: (payload.assetCaptures ?? []).map((a) => ({
          assetRowId: a.assetRowId,
          km: a.km ?? null,
          usageHours: a.usageHours ?? null,
          isRoundTrip: a.isRoundTrip ?? false,
          notes: a.notes ?? null,
        })),
        attachTasks: (payload.attachTasks ?? []).map((a) => ({
          // UnassignedJob.id IS the WorkOrderTaskId for an unplanned backlog task.
          workOrderTaskId: a.task.id,
          scaffoldId: a.task.scaffoldId ?? null,
          status: DispatchTaskStatus[a.status],
          perWorkerHours: a.perWorkerHours ?? null,
          // Per-rate-band hours override ("split by role"); null = uniform.
          roleHours: a.roleHours ?? null,
          notes: a.notes ?? null,
        })),
        adhocTasks: (payload.adhocTasks ?? []).map((a) => ({
          description: a.description,
          scaffoldId: a.scaffoldId,
          taskType: a.taskType,
          status: DispatchTaskStatus[a.status],
          perWorkerHours: a.perWorkerHours ?? null,
          notes: a.notes ?? null,
          isBillable: a.isBillable,
          chargeUpType: a.chargeUpType ?? null,
        })),
        rowVersion: null,
      });
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.captureActivity(date, payload);
  },

  /** Roll over selected incomplete tasks onto target dates. Creates one
   *  continuation activity per (targetDate, targetCrewId) bucket. */
  async rollOverActivity(date: string, payload: RollOverPayload): Promise<void> {
    if (useRealApi()) {
      // BE endpoint POST /DispatchActivity/roll-over already exists (Phase 5c):
      // it validates each task is Partial, then creates/reuses a continuation
      // activity per (targetDate, targetCrewId) in the same RootActivityId chain
      // and adds the task as Pending (same scaffold lineage so day-1 + day-2
      // both bill, no double-bill). Maps 1:1 to the FE payload — `taskId` here IS
      // the DbDispatchActivityTask.Id the day-read mapper surfaced as `taskId`.
      const res = await backendApi.activity.rollOver({
        sourceActivityId: payload.sourceActivityId,
        tasks: payload.tasks.map((t) => ({
          taskId: t.taskId,
          targetDate: t.targetDate,
          targetCrewId: t.targetCrewId ?? null,
        })),
      });
      ensureOk(res);
      return;
    }
    await wait(LATENCY_MS);
    mockStore.rollOverActivity(date, payload);
  },

  /**
   * Day-level cartage capture (vehicle km billed per day, allocated per WO).
   *
   * Real path: POST /DispatchActivity/day-cartage — the backend creates one
   * DbCartageJob per vehicle (odometer-derived or direct km × matrix rate −
   * trip discount) and allocates it across the WorkOrders the vehicle
   * serviced (one CartageJobSplit per WO), which is exactly what
   * InvoiceGenerationService reads. Idempotent until invoiced.
   *
   * Mock path: the mock store has no cartage ledger, so this is a no-op
   * (after the standard latency) — the demo UX still flows (toast fires,
   * panel resets). Returns the created/updated cartage-job count so the
   * toast can report it on the real path.
   */
  async captureDayCartage(req: CaptureDayCartageRequest): Promise<number> {
    if (useRealApi()) {
      const res = await backendApi.activity.captureDayCartage(req);
      ensureOk(res);
      return res.result ?? 0;
    }
    await wait(LATENCY_MS);
    // Mock mode: nothing to persist (no cartage store) — report the count
    // of vehicles captured so the toast still has something meaningful.
    return req.vehicles.length;
  },

  /**
   * TEST/ADMIN: un-capture one activity → flip it back to capturable and drop
   * its UNINVOICED downstream rows. Real API only (the server gates it to the
   * reset-admin / super-admin); mock is a no-op (this tool targets real-API
   * testing). Returns 1 if reset, 0 if skipped/invoiced.
   */
  async uncaptureActivity(activityId: string): Promise<number> {
    if (useRealApi()) {
      const res = await backendApi.activity.uncapture(activityId);
      ensureOk(res);
      return res.result ?? 0;
    }
    await wait(LATENCY_MS);
    return 0;
  },

  /** TEST/ADMIN: reset every capture on a day. Returns the count reset. */
  async resetDayCaptures(date: string): Promise<number> {
    if (useRealApi()) {
      const res = await backendApi.activity.resetDay(date);
      ensureOk(res);
      return res.result ?? 0;
    }
    await wait(LATENCY_MS);
    return 0;
  },

  /**
   * Day-wide capture status snapshot. Honours the real-API flag so the Day
   * Capture popup reads the SAME source as the board (GET /capture-summary).
   * Previously mock-only, which made the popup show "No activities to capture"
   * whenever the board itself was running on the real API. The mock + backend
   * CaptureSummary shapes are 1:1 by design, so no mapping is needed.
   */
  async getCaptureSummary(date: string): Promise<CaptureSummary> {
    if (useRealApi()) {
      const be = await backendApi.board.getCaptureSummary(date);
      // Backend leaves activityNumber/title optional + capturedAt nullable; the
      // FE shape is slightly stricter on those three, so coerce them.
      return {
        ...be,
        activities: be.activities.map((a) => ({
          ...a,
          activityNumber: a.activityNumber ?? '',
          title: a.title ?? '',
          capturedAt: a.capturedAt ?? undefined,
        })),
      };
    }
    await wait(LATENCY_MS);
    return mockStore.getCaptureSummary(date);
  },

  /* ---------- Board-first task creation (Sprint A — mock-first) ----------
   *
   * MOCK-ONLY. Creates a backlog WorkOrderTask that surfaces in the
   * Unassigned rail. The real backend endpoint exists
   * (POST /api/v1/WorkOrderTask, manual mode: QuoteItemId null →
   * ScaffoldId + WorkItemType required, WorkItemType derived from the
   * ScaffoldTaskType — Erect/Modify/Dismantle→Structure, others→Custom,
   * never Labour). Wiring deferred to 7g-REAL after client sign-off, same
   * as the capture flow.
   */
  async createBacklogTask(payload: BacklogTaskCreatePayload): Promise<void> {
    await wait(LATENCY_MS);
    mockStore.createBacklogTask(payload);
  },

  /* ---------- Demo-data reset ---------- */

  /**
   * Wipe the entire mock store — every crew, every activity, every
   * date — and clear localStorage. Powers the toolbar's Reset button
   * for "give me a clean board to demo from."
   *
   * No latency on this one — it's a deliberate user gesture and the
   * faster the empty-board hero appears, the better the demo feels.
   */
  async reset(): Promise<void> {
    mockStore.reset();
  },
};
